/**
 * Phase 4: Cross-Tab Navigation Utility
 *
 * Provides unified navigation between views with contextual linking:
 * - Tab switching (workspace, report, graph)
 * - Node highlighting in Business Graph
 * - Scroll-to-section within tabs
 * - Sub-tab switching within Strategy Workspace (MECE, Strategy Map)
 */

import type { CenterTab } from '@contexts/TabNavigationContext'
import type { AppDispatch } from '@state/store'
import { setSelectedNode } from '@state/slices/graphSlice'

// Workspace sub-tabs
export type WorkspaceSubTab = 'mece' | 'strategy-map'

// Navigation target types
export interface NavigationTarget {
  tab: CenterTab
  workspaceSubTab?: WorkspaceSubTab
  nodeId?: string  // For highlighting in Business Graph
  sectionId?: string  // For scrolling to a section
  pillarName?: string  // For scrolling to a specific MECE pillar
}

// Navigation event for analytics/debugging
export interface NavigationEvent {
  from: CenterTab
  to: NavigationTarget
  timestamp: number
  context?: string
}

/**
 * Navigate to a specific tab with optional sub-navigation
 */
export function navigateTo(
  target: NavigationTarget,
  navigateToTab: (tab: CenterTab) => void,
  dispatch?: AppDispatch,
  setWorkspaceSubTab?: (subTab: WorkspaceSubTab) => void
): void {
  // 1. Switch to the target tab
  navigateToTab(target.tab)

  // 2. If going to Business Graph with a node ID, highlight the node
  if (target.tab === 'graph' && target.nodeId && dispatch) {
    setTimeout(() => {
      dispatch(setSelectedNode(target.nodeId!))
    }, 100) // Small delay to ensure tab is rendered
  }

  // 3. If going to workspace with a sub-tab specified, switch sub-tab
  if (target.tab === 'workspace' && target.workspaceSubTab && setWorkspaceSubTab) {
    setTimeout(() => {
      setWorkspaceSubTab(target.workspaceSubTab!)
    }, 50)
  }

  // 4. Scroll to section if specified
  if (target.sectionId) {
    setTimeout(() => {
      scrollToSection(target.sectionId!)
    }, 200) // Longer delay to ensure content is rendered
  }

  // 5. Scroll to pillar if specified
  if (target.pillarName) {
    setTimeout(() => {
      scrollToPillar(target.pillarName!)
    }, 200)
  }
}

/**
 * Navigate from Analysis Report to MECE Table in Strategy Workspace
 */
export function navigateToMeceTable(
  navigateToTab: (tab: CenterTab) => void,
  setWorkspaceSubTab?: (subTab: WorkspaceSubTab) => void,
  pillarName?: string
): void {
  navigateTo({
    tab: 'workspace',
    workspaceSubTab: 'mece',
    pillarName
  }, navigateToTab, undefined, setWorkspaceSubTab)
}

/**
 * Navigate from Analysis Report to Strategy Map in Strategy Workspace
 */
export function navigateToStrategyMap(
  navigateToTab: (tab: CenterTab) => void,
  setWorkspaceSubTab?: (subTab: WorkspaceSubTab) => void
): void {
  navigateTo({
    tab: 'workspace',
    workspaceSubTab: 'strategy-map'
  }, navigateToTab, undefined, setWorkspaceSubTab)
}

/**
 * Navigate to Business Graph and highlight a specific node
 */
export function navigateToGraphNode(
  nodeId: string,
  navigateToTab: (tab: CenterTab) => void,
  dispatch: AppDispatch
): void {
  navigateTo({
    tab: 'graph',
    nodeId
  }, navigateToTab, dispatch)
}

/**
 * Navigate to a specific MECE tactic in Strategy Workspace
 * Uses tactic reference format (e.g., "1.2", "2.3")
 * Jan 2026: MECE-Strategy Map Tactic Reference Integration
 */
export function navigateToMeceTactic(
  tacticRef: string,
  navigateToTab: (tab: CenterTab) => void,
  setWorkspaceSubTab?: (subTab: WorkspaceSubTab) => void
): void {
  // Parse reference (e.g., "1.2" -> pillarRank=1, tacticPriority=2)
  const parts = tacticRef.split('.')
  const pillarRank = parseInt(parts[0], 10) || 1
  const tacticPriority = parseInt(parts[1], 10) || 1

  // Navigate to workspace with MECE sub-tab
  navigateTo({
    tab: 'workspace',
    workspaceSubTab: 'mece'
  }, navigateToTab, undefined, setWorkspaceSubTab)

  // Scroll to specific tactic using DOM ID
  // Format: mece-tactic-P{pillarRank}T{tacticPriority}
  setTimeout(() => {
    const tacticElementId = `mece-tactic-P${pillarRank}T${tacticPriority}`
    scrollToSection(tacticElementId)
  }, 300)  // Longer delay to ensure MECE table is rendered
}

/**
 * Scroll to a section by ID within the current tab
 */
export function scrollToSection(sectionId: string): void {
  const element = document.getElementById(sectionId)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' })

    // Add highlight effect
    element.classList.add('highlight-flash')
    setTimeout(() => {
      element.classList.remove('highlight-flash')
    }, 2000)
  }
}

/**
 * Scroll to a pillar by name in the MECE Table
 */
export function scrollToPillar(pillarName: string): void {
  // Find pillar by matching text content
  const pillars = document.querySelectorAll('.mece-pillar')
  for (const pillar of pillars) {
    const header = pillar.querySelector('.pillar-header h5')
    if (header && header.textContent?.toLowerCase().includes(pillarName.toLowerCase())) {
      pillar.scrollIntoView({ behavior: 'smooth', block: 'start' })

      // Add highlight effect
      pillar.classList.add('highlight-flash')
      setTimeout(() => {
        pillar.classList.remove('highlight-flash')
      }, 2000)
      break
    }
  }
}

/**
 * Create a link handler for navigation
 * Use this to create onClick handlers for navigation links
 */
export function createNavigationHandler(
  target: NavigationTarget,
  navigateToTab: (tab: CenterTab) => void,
  dispatch?: AppDispatch,
  setWorkspaceSubTab?: (subTab: WorkspaceSubTab) => void
): () => void {
  return () => {
    navigateTo(target, navigateToTab, dispatch, setWorkspaceSubTab)
  }
}

/**
 * Parse navigation links in markdown content
 * Converts [text](#target) links to navigation targets
 */
export function parseNavigationLink(href: string): NavigationTarget | null {
  if (!href.startsWith('#')) return null

  const target = href.slice(1).toLowerCase()

  switch (target) {
    case 'analysis-report':
    case 'report':
      return { tab: 'report' }
    case 'strategy-workspace':
    case 'workspace':
      return { tab: 'workspace' }
    case 'mece-table':
    case 'mece':
      return { tab: 'workspace', workspaceSubTab: 'mece' }
    case 'strategy-map':
      return { tab: 'workspace', workspaceSubTab: 'strategy-map' }
    case 'business-graph':
    case 'graph':
      return { tab: 'graph' }
    default:
      // Check if it's a document reference (format: "document:process:node_id")
      if (target.startsWith('document:')) {
        const parts = target.split(':')
        if (parts.length >= 3) {
          const docType = parts[1] as 'process' | 'ksa' | 'dca'
          const nodeId = parts.slice(2).join(':')  // Handle node IDs with colons
          return { tab: 'document', sectionId: `${docType}:${nodeId}` }
        }
      }
      // Check if it's a MECE tactic reference (format: "1.2", "2.3")
      if (/^\d+\.\d+$/.test(target)) {
        return { tab: 'workspace', workspaceSubTab: 'mece', sectionId: `mece-tactic-P${target.split('.')[0]}T${target.split('.')[1]}` }
      }
      // Check if it's a node reference (starts with common node ID patterns)
      if (target.endsWith('_id') || target.includes('node_')) {
        return { tab: 'graph', nodeId: target }
      }
      return null
  }
}
